// Back from Paystack: what the confirmation reader's answer means for the
// guest (get_booking_confirmation_by_payment_reference, 20261001160010). Pure:
// no React, no network, no storage, no clock (every time is passed in).
// Pinned by e2e/confirmation-outcome-logic.spec.ts; the same file on main and
// on staging.
//
// The reader says ok:true ONLY when a booking this payment paid for (the
// bookings it names) is confirmed. Otherwise:
//   payment_received_not_confirmed  paid, and the webhook is still deciding
//                                   (it confirms the booking, or refunds the
//                                   payment: a payment after the hold ended
//                                   whose slot could not be re-secured);
//   payment_refunded                the money is going back; with
//                                   booking_confirmed the booking stands;
//                                   refund_in_full false when the refund on
//                                   file is only part of the payment;
//   payment_superseded              paid on a page opened before the booking
//                                   changed (20261003200010): it confirmed
//                                   nothing and goes back in full
//                                   (refund_cents; refund_state succeeded /
//                                   pending / null); with booking_held the
//                                   booking is still held until held_until,
//                                   at amount_due_cents (the cart's total
//                                   now), for party_size when line_count is
//                                   1, and can_pay_updated says the /pay link
//                                   is there (superseded_payment_pay_link);
//                                   with booking_confirmed and booking_paid
//                                   another payment paid for it; confirmed
//                                   but not paid, the venue collects it;
// and, as before, payment_not_paid (not paid yet: Paystack sends the browser
// back before, or as, it tells the server), payment_not_found, not_a_booking.
//
// "Confirmed", and the Google Ads conversion with the paid amount, are for a
// confirmed answer only.

export interface PaystackReturnBooking {
  id: string;
  reference: string | null;
  status: string;
  service_name: string;
}

export interface PaystackReturnTicket {
  qr_code: string;
  status: string;
  ticket_type: string;
  event_name: string;
  guest_name: string | null;
}

export type PaystackReturnConfirmation =
  | {
      ok: true;
      customer: {
        first_name: string | null;
        email: string | null;
        phone: string | null;
      };
      primary_booking_reference: string | null;
      /** Paid amount (cents) + currency — drive the client-side Google Ads conversion. */
      amount_cents?: number | null;
      currency?: string | null;
      bookings: PaystackReturnBooking[];
      /** Issued QR tickets for an event-ticket purchase (empty for plain bookings). */
      tickets?: PaystackReturnTicket[];
      /** Absolute /manage/<token> URL that renders the QR tickets. */
      manage_url?: string | null;
    }
  | {
      ok: false;
      reason:
        | "payment_not_found"
        | "payment_not_paid"
        | "not_a_booking"
        | "payment_received_not_confirmed"
        | "payment_refunded"
        | "payment_superseded"
        | string;
      /** payment_refunded / payment_superseded: the booking this payment paid for stands. */
      booking_confirmed?: boolean;
      /** payment_refunded: false when the refund on file is only part of the payment. */
      refund_in_full?: boolean;
      /** payment_superseded: what goes back (always the whole payment). */
      refund_cents?: number | null;
      currency?: string | null;
      /** payment_superseded: the booking is still held for the updated total. */
      booking_held?: boolean;
      held_until?: string | null;
      amount_due_cents?: number | null;
      party_size?: number | null;
      /** payment_superseded: how many lines are held (party_size only for one). */
      line_count?: number | null;
      /** payment_superseded: the booking is confirmed AND paid (by another payment). */
      booking_paid?: boolean;
      /** payment_superseded: a /pay link for the updated total can be had from here. */
      can_pay_updated?: boolean;
      /** payment_superseded: where its refund stands (null: not on file yet). */
      refund_state?: "succeeded" | "pending" | null;
    };

export const RECEIVED_NOT_CONFIRMED = "payment_received_not_confirmed";
export const PAYMENT_REFUNDED = "payment_refunded";
export const PAYMENT_SUPERSEDED = "payment_superseded";

/**
 * The reader's answer as the widget trusts it. An ok:true answer must name
 * what was confirmed (a booking, a ticket or the booking reference): the
 * reader before 20261001160010 said ok:true, with the amount, for a payment
 * with no confirmed booking at all, so an ok:true that names none is read as
 * "received, not confirmed" and the widget still never says Confirmed without
 * a booking, whichever of the widget and the database is deployed first.
 * Anything unreadable is bad_answer (not paid, as far as the page is
 * concerned).
 */
export function normalizeConfirmation(data: unknown): PaystackReturnConfirmation {
  if (!data || typeof data !== "object" || Array.isArray(data)) return { ok: false, reason: "bad_answer" };
  const a = data as Record<string, unknown>;
  if (a.ok === true) {
    const named =
      (Array.isArray(a.bookings) && a.bookings.length > 0) ||
      (Array.isArray(a.tickets) && a.tickets.length > 0) ||
      (typeof a.primary_booking_reference === "string" && a.primary_booking_reference.length > 0);
    if (!named) return { ok: false, reason: RECEIVED_NOT_CONFIRMED };
    return data as PaystackReturnConfirmation;
  }
  if (a.ok === false && typeof a.reason === "string") return data as PaystackReturnConfirmation;
  return { ok: false, reason: "bad_answer" };
}

/**
 * What the answer means:
 *   confirmed        a booking is confirmed: the confirmation (and the ad conversion);
 *   received         paid, not confirmed yet: "Payment received — we're
 *                    confirming your booking…", and keep asking;
 *   refunded         no booking, the payment is being refunded, with a way
 *                    back to choose another time (a fresh cart);
 *   refunded_booked  the booking stands, this payment is being refunded;
 *   superseded       paid on a page opened before the booking changed: it
 *                    goes back in full, and the updated total can be paid
 *                    while the booking is held;
 *   not_a_booking    a gift card or the like: its own flow owns the return;
 *   waiting          not paid yet, not found, or no answer: as before.
 */
export type ConfirmationOutcome =
  | "confirmed"
  | "received"
  | "refunded"
  | "refunded_booked"
  | "superseded"
  | "not_a_booking"
  | "waiting";

export function confirmationOutcome(answer: PaystackReturnConfirmation): ConfirmationOutcome {
  if (answer.ok) return "confirmed";
  switch (answer.reason) {
    case RECEIVED_NOT_CONFIRMED:
      return "received";
    case PAYMENT_REFUNDED:
      return answer.booking_confirmed === true ? "refunded_booked" : "refunded";
    case PAYMENT_SUPERSEDED:
      return "superseded";
    case "not_a_booking":
      return "not_a_booking";
    default:
      return "waiting";
  }
}

/**
 * Whether a refund answer gives the whole payment back. The reader says so
 * (refund_in_full); an answer without it is read as in full, the reader's
 * own default for its refunds.
 */
export function refundInFull(answer: PaystackReturnConfirmation): boolean {
  return answer.ok || answer.reason !== PAYMENT_REFUNDED || answer.refund_in_full !== false;
}

/** Nothing more will change what the page says: stop asking. */
export function isFinalOutcome(outcome: ConfirmationOutcome): boolean {
  return (
    outcome === "confirmed" ||
    outcome === "refunded" ||
    outcome === "refunded_booked" ||
    outcome === "superseded" ||
    outcome === "not_a_booking"
  );
}

// ─── The words ──────────────────────────────────────────────────────────────

export const RECEIVED_MESSAGE = "Payment received — we're confirming your booking…";
export const RECEIVED_HINT = "This usually takes a few seconds. Please don't pay again.";
export const RECEIVED_LATE_MESSAGE = "Payment received. We're still confirming your booking.";
/**
 * No promise: the booking is not confirmed yet, and may never be (paid after
 * the hold ended). Nothing on the server promises a refund or a message in
 * every case (a refund Finance rejects, a hold the guest cancelled before
 * paying), so the guest is told where to turn, not what will happen.
 */
export const RECEIVED_LATE_HINT =
  "Once it's confirmed, the confirmation will arrive by email or WhatsApp. If nothing arrives soon, please contact the venue. Please don't pay again.";
/** No booking: the money goes back, in full, by itself. */
export const REFUNDED_MESSAGE = "We couldn't keep this booking, so your payment is being refunded in full.";
/** No booking, and the refund on file is only part of the payment: never "in full". */
export const REFUNDED_PART_MESSAGE = "We couldn't keep this booking, so a refund of your payment is being arranged.";
export const REFUNDED_HINT = "You don't need to do anything; it can take a few working days to reach your card.";
/** The booking stands; this payment goes back. */
export const REFUNDED_BOOKED_MESSAGE = "Your booking is confirmed, and this payment is being refunded in full.";
export const REFUNDED_BOOKED_PART_MESSAGE = "Your booking is confirmed, and a refund of this payment is being arranged.";
/**
 * Never why the booking stands (another payment paid for it, or the venue
 * kept it after refunding the only payment): the same plain words as a
 * refund with no booking.
 */
export const REFUNDED_BOOKED_HINT = REFUNDED_HINT;
/** The way back from a refund with no booking. */
export const CHOOSE_ANOTHER_TIME = "Choose another time";

/** The refund's headline: in full only when the reader says the whole payment goes back. */
export function refundMessage(bookingConfirmed: boolean, inFull = true): string {
  if (bookingConfirmed) return inFull ? REFUNDED_BOOKED_MESSAGE : REFUNDED_BOOKED_PART_MESSAGE;
  return inFull ? REFUNDED_MESSAGE : REFUNDED_PART_MESSAGE;
}

export type ReturnWords = { message: string; hint: string | null };

/** The words for an answer that is not a confirmation (null: nothing to say yet). */
export function outcomeWords(
  outcome: ConfirmationOutcome,
  { late = false, inFull = true }: { late?: boolean; inFull?: boolean } = {},
): ReturnWords | null {
  switch (outcome) {
    case "received":
      return late ? { message: RECEIVED_LATE_MESSAGE, hint: RECEIVED_LATE_HINT } : { message: RECEIVED_MESSAGE, hint: RECEIVED_HINT };
    case "refunded":
      return { message: refundMessage(false, inFull), hint: REFUNDED_HINT };
    case "refunded_booked":
      return { message: refundMessage(true, inFull), hint: REFUNDED_BOOKED_HINT };
    default:
      return null;
  }
}

// ─── Paid on an out-of-date page (payment_superseded, 20261003200010) ──────

/** "R1,000", "R1,500.50", "USD 20": the same on every device (never the browser's locale). */
export function formatAmount(cents: number | null | undefined, currency: string | null | undefined): string {
  const value = Math.max(0, Number(cents ?? 0)) / 100;
  const whole = Number.isInteger(value);
  const text = value.toLocaleString("en-US", {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  });
  const code = (currency || "ZAR").toUpperCase();
  return code === "ZAR" ? `R${text}` : `${code} ${text}`;
}

/** "14:32" in the venue's time zone (the hold is minutes long: the time says it). */
export function formatHoldTime(iso: string | null | undefined, timeZone = "Africa/Johannesburg"): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  let zone = timeZone || "Africa/Johannesburg";
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: zone });
  } catch {
    zone = "Africa/Johannesburg";
  }
  return new Intl.DateTimeFormat("en-GB", { timeZone: zone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(at);
}

export type SupersededWords = {
  message: string;
  hint: string;
  /** "Pay R1,500": the way to pay the updated total, while the booking is held. */
  payLabel: string | null;
  /**
   * Held, but no /pay link can be had from here (a spa treatment rode on the
   * payment): where to pay instead. Null otherwise.
   */
  payElsewhere: string | null;
  /** No booking any more: the way back is to choose a time again. */
  chooseAnotherTime: boolean;
};

/**
 * What a guest who paid an out-of-date page is told, plainly: this payment
 * goes back in full (or went back: refund_state succeeded), and what to do
 * next: nothing (another payment paid for the booking), nothing online
 * (confirmed but not paid: the venue collects it), pay the updated total
 * while the booking is held (the cart's total now; "for 3" only when one
 * line is held), or choose a time again. Never how long a refund takes: it
 * waits for the venue's approval first.
 */
export function supersededWords(
  answer: PaystackReturnConfirmation,
  { timeZone = "Africa/Johannesburg" }: { timeZone?: string } = {},
): SupersededWords {
  const a = answer.ok ? null : answer;
  const currency = a?.currency ?? "ZAR";
  const refund = typeof a?.refund_cents === "number" ? a.refund_cents : null;
  const amount = refund !== null && refund > 0 ? `${formatAmount(refund, currency)} ` : "";
  const message =
    a?.refund_state === "succeeded"
      ? `Your booking changed after this payment page opened, so this ${amount}payment has been refunded in full.`
      : `Your booking changed after this payment page opened, so this ${amount}payment is being refunded in full.`;
  if (a?.booking_confirmed === true) {
    return {
      message,
      hint:
        a.booking_paid === true
          ? "Your booking is confirmed by your other payment. You don't need to do anything."
          : "Your booking is confirmed, but this payment didn't pay for it. The venue will be in touch about paying for your booking.",
      payLabel: null,
      payElsewhere: null,
      chooseAnotherTime: false,
    };
  }
  if (a?.booking_held === true) {
    const until = formatHoldTime(a.held_until, timeZone);
    const lines = typeof a.line_count === "number" ? a.line_count : null;
    const party = (lines === null || lines === 1) && typeof a.party_size === "number" && a.party_size > 0 ? a.party_size : null;
    const due = typeof a.amount_due_cents === "number" && a.amount_due_cents > 0 ? a.amount_due_cents : null;
    const what = party ? `Your booking for ${party}` : "Your booking";
    const held = until ? `${what} is held until ${until}` : `${what} is still held`;
    const payable = a.can_pay_updated !== false;
    return {
      message,
      hint: due
        ? `${held} — pay ${formatAmount(due, currency)} to confirm it.`
        : `${held} — pay the updated total to confirm it.`,
      payLabel: payable ? (due ? `Pay ${formatAmount(due, currency)}` : "Pay the updated total") : null,
      payElsewhere: payable
        ? null
        : "To pay the updated total, go back to the page where you made this booking, or ask the venue for a payment link.",
      chooseAnotherTime: false,
    };
  }
  return {
    message,
    hint: "Your booking is no longer held. Please choose a time again.",
    payLabel: null,
    payElsewhere: null,
    chooseAnotherTime: true,
  };
}

/** Pay the updated total could not open the payment page. */
export const SUPERSEDED_PAY_FAILED = "We couldn't open the payment page just now. Please try again.";
/** Pay the updated total: the button while the payment page opens. */
export const SUPERSEDED_PAY_BUSY = "Opening the payment page…";

// ─── Choose another time: a fresh cart ─────────────────────────────────────

/**
 * The holds Choose another time lets go of (every line's booking), before
 * the tab takes a new cart token. The refunded payment stays with its cart:
 * a new booking paid on that same cart token is ranked behind it by the
 * webhook's double-charge guard and refunded too, so the new time is chosen
 * on a fresh cart.
 */
export function holdsToRelease(items: readonly { metadata?: Record<string, unknown> | null }[]): string[] {
  const ids: string[] = [];
  for (const item of items) {
    const id = item.metadata?.booking_id;
    if (typeof id === "string" && id.length > 0 && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

// ─── Asking again ───────────────────────────────────────────────────────────

/** The return's usual asks, ms after the first (about 20 seconds). */
export const RETURN_ASK_AT_MS: readonly number[] = [0, 1500, 3000, 5000, 7500, 10_000, 13_000, 16_500, 20_000];
/** While the payment is received and not yet confirmed: every 5 seconds up to 3 minutes. */
export const RECEIVED_ASK_EVERY_MS = 5000;
export const RECEIVED_ASK_FOR_MS = 180_000;
/**
 * After that (the page says the late words), every 30 seconds up to 30
 * minutes from the return, so a refund, or a confirmation, still shows by
 * itself.
 */
export const RECEIVED_LATE_ASK_EVERY_MS = 30_000;
export const RECEIVED_LATE_ASK_FOR_MS = 1_800_000;
/** The late asks as a schedule (ms after the late words), for a poller that takes one. */
export const RECEIVED_LATE_ASK_AT_MS: readonly number[] = Array.from(
  { length: Math.floor((RECEIVED_LATE_ASK_FOR_MS - RECEIVED_ASK_FOR_MS) / RECEIVED_LATE_ASK_EVERY_MS) },
  (_, i) => (i + 1) * RECEIVED_LATE_ASK_EVERY_MS,
);

/** A payment still received this long after the return gets the late words. */
export function isLateReceived(elapsedMs: number, forMs = RECEIVED_ASK_FOR_MS): boolean {
  return elapsedMs >= forMs;
}

/**
 * When to ask the reader again (ms from now), `elapsedMs` after the first
 * ask, or null: stop.
 *   final     never again;
 *   received  the usual asks, then every 5 seconds until 3 minutes (the page
 *             then says the late words), then every 30 seconds until 30
 *             minutes;
 *   waiting   the usual asks (about 20 seconds), then stop.
 */
export function nextAskIn(
  outcome: ConfirmationOutcome,
  elapsedMs: number,
  {
    askAt = RETURN_ASK_AT_MS,
    everyMs = RECEIVED_ASK_EVERY_MS,
    forMs = RECEIVED_ASK_FOR_MS,
    lateEveryMs = RECEIVED_LATE_ASK_EVERY_MS,
    lateForMs = RECEIVED_LATE_ASK_FOR_MS,
  }: {
    askAt?: readonly number[];
    everyMs?: number;
    forMs?: number;
    lateEveryMs?: number;
    lateForMs?: number;
  } = {},
): number | null {
  if (isFinalOutcome(outcome)) return null;
  const elapsed = Math.max(0, elapsedMs);
  const slot = askAt.find((at) => at > elapsed);
  if (slot !== undefined) return slot - elapsed;
  if (outcome !== "received") return null;
  if (elapsed + everyMs <= forMs) return everyMs;
  if (elapsed + lateEveryMs <= lateForMs) return lateEveryMs;
  return null;
}
